/**
 * "Come back without guilt": pure logic for the Home card.
 *
 * Frontend only. The push side (send-lesson-reminders) decides its own day in
 * SQL (comeback_push_user_ids), so nothing here is shared with Deno.
 *
 * All day arithmetic is on Israel calendar days, never on elapsed hours: a day
 * is 23 or 25 hours long when the clocks change.
 */

import { todayInIsrael } from '@/lib/dateUtils';

/** How many Israel calendar days away before the card greets the member. */
const COMEBACK_CARD_MIN_DAYS = 7;

/** Whole Israel calendar days from `from` to `to`. Negative when `to` is earlier. */
export function israelDayDiff(from: Date, to: Date): number {
  const [y1, m1, d1] = todayInIsrael(from).split('-').map(Number);
  const [y2, m2, d2] = todayInIsrael(to).split('-').map(Number);
  return (Date.UTC(y2, m2 - 1, d2) - Date.UTC(y1, m1 - 1, d1)) / 86_400_000;
}

/** Identifies one absence: the Israel date of the last activity before it. */
export function comebackAnchor(lastActivityAt: string): string {
  return todayInIsrael(new Date(lastActivityAt));
}

export function shouldShowComebackCard(a: {
  /** Last core activity before today's Israel midnight, as decided in SQL. */
  lastActivityBeforeToday: string | null;
  now: Date;
  /** The anchor of the absence the member already dismissed, if any. */
  dismissedAnchor: string | null;
}): boolean {
  if (!a.lastActivityBeforeToday) return false;
  const last = new Date(a.lastActivityBeforeToday);
  if (Number.isNaN(last.getTime())) return false;
  if (israelDayDiff(last, a.now) < COMEBACK_CARD_MIN_DAYS) return false;
  return todayInIsrael(last) !== a.dismissedAnchor;
}
